
import { SmartSplitSuggestion } from '../types';

// --- AUDIO UTILS (PCM Handling) ---

export function decodeBase64(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

export async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

// --- CONTEXT FORMATTERS ---

export const formatLogisticContext = (analysis?: SmartSplitSuggestion | null) => {
  if (!analysis) return "Análise logística indisponível no momento.";

  const routesSummary = analysis.routes.map(r => {
    const cleanAssignee = r.type === 'third' ? r.assigneeName.split(' #')[0] : r.assigneeName;
    return {
      assignee: cleanAssignee,
      type: r.type,
      orderCount: r.orders.length
    };
  });

  let strategyStatus = "STANDARD_DISPATCH";
  let strategyRationale = "Fluxo normal de despacho.";

  const decisionReason = analysis.metrics?.cashDelta?.reason || 'UNKNOWN';
  const reasonMap: Record<string, string> = {
    'CHEAPER': "Opção mais econômica no momento.",
    'CONSTRAINT_NO_FLEET': "Frota própria indisponível ou fora do raio.",
    'CAPACITY_PRESSURE': "Frota sobrecarregada; terceirização necessária para manter o fluxo.",
    'SLA_PRIORITY': "Prioridade total ao prazo de entrega (SLA).",
    'RETENTION_STRATEGY': "Retenção estratégica para otimização de rota.",
    'UNKNOWN': "Decisão baseada em disponibilidade e custo padrão."
  };

  if (analysis.smartWaitAnalysis?.active) {
    strategyStatus = "WAITING_KITCHEN_FOR_BATCH";
    strategyRationale = "Paciência Estratégica: Segurando o início da produção para esperar vizinhos de rota e economizar pernas de entrega.";
  } else if (analysis.sequentialOpportunity?.active) {
    strategyStatus = "BOOMERANG_OPPORTUNITY";
    strategyRationale = "Bumerangue: O motorista fará uma entrega rápida e voltará para o restante da rota, otimizando o tempo da frota.";
  } else if (analysis.heldOrders.length > 0) {
    strategyStatus = "RETENTION_ACTIVE";
    strategyRationale = "Sincronia de Balcão: Pedidos prontos aguardando os que estão saindo da cozinha para envio em lote.";
  } else {
    // Universal Justification for Standard Dispatch
    strategyRationale = reasonMap[decisionReason] || reasonMap['UNKNOWN'];
    if (analysis.isSplitRecommended) {
      strategyRationale += " Sugestão de divisão inteligente entre frota e terceiros.";
    } else if (analysis.thirdParty.length > 0) {
      strategyRationale += " Envio externo recomendado.";
    } else {
      strategyRationale += " Frota própria selecionada.";
    }
  }

  return JSON.stringify({
    strategy_status: strategyStatus,
    strategy_rationale: strategyRationale,
    boomerang_details: analysis.sequentialOpportunity ? {
      driver_name: analysis.sequentialOpportunity.driverName,
      orders_outbound_count: analysis.sequentialOpportunity.trip1.length,
      orders_return_count: analysis.sequentialOpportunity.trip2.length
    } : null,
    held_orders_count: analysis.heldOrders.length,
    routes_summary: routesSummary,
    algorithm_reasoning: analysis.waitSuggestion?.message || "Otimização de custo padrão.",
    total_savings: analysis.metrics?.cashDelta?.signedDelta.toFixed(2) || "0.00",
  });
};
